/**
 * Field utility functions for input type mapping and validation.
 * Schema-agnostic — works with both v1 fieldType and v2 fieldSubtype values.
 */

export interface InputAttributes {
  type: string;
  inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters' | 'off' | 'on';
  autoCorrect?: 'on' | 'off';
  autoComplete?: string;
}

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PHONE_REGEX = /^[\d\s\-+()]+$/;

/**
 * Builds a RegExp from a schema validation string. Editor/config values may be
 * stored either as a bare pattern ("^a+$") or in literal form ("/^a+$/i") —
 * literal form is parsed so its flags apply instead of matching as raw chars.
 */
export function buildValidationRegExp(pattern: string): RegExp {
  const literalMatch = pattern.match(/^\/(.*)\/([dgimsuvy]*)$/s);
  if (literalMatch) {
    return new RegExp(literalMatch[1], literalMatch[2]);
  }
  return new RegExp(pattern);
}

/** Maps explicit KeyboardType enum value to InputAttributes */
const KEYBOARD_TYPE_MAP: Record<string, InputAttributes> = {
  email: { type: 'email', inputMode: 'email', autoCapitalize: 'none', autoCorrect: 'off', autoComplete: 'email' },
  numeric: { type: 'text', inputMode: 'numeric', autoCapitalize: 'off', autoCorrect: 'off', autoComplete: 'off' },
  decimal: { type: 'text', inputMode: 'decimal', autoCapitalize: 'off', autoCorrect: 'off', autoComplete: 'off' },
  phone: { type: 'tel', inputMode: 'tel', autoCapitalize: 'off', autoCorrect: 'off', autoComplete: 'tel' },
  url: { type: 'url', inputMode: 'url', autoCapitalize: 'none', autoCorrect: 'off', autoComplete: 'url' },
};

/**
 * Maps field type to HTML input attributes for optimal mobile keyboard and UX.
 * Accepts both v1 fieldType and v2 fieldSubtype values.
 * When explicit keyboardType is provided (schema 2.7.0), it overrides the auto-derived value.
 */
export function getFieldInputAttributes(fieldType?: string, keyboardType?: string): InputAttributes {
  // Explicit keyboardType override (schema 2.7.0)
  if (keyboardType && keyboardType !== 'default' && KEYBOARD_TYPE_MAP[keyboardType]) {
    return KEYBOARD_TYPE_MAP[keyboardType];
  }
  switch (fieldType) {
    case 'Email':
    case 'email':
      return {
        type: 'email',
        inputMode: 'email',
        autoCapitalize: 'none',
        autoCorrect: 'off',
        autoComplete: 'email',
      };

    case 'Phone':
    case 'phone':
      return {
        type: 'tel',
        inputMode: 'tel',
        autoCapitalize: 'off',
        autoCorrect: 'off',
        autoComplete: 'tel',
      };

    case 'Number':
    case 'number':
      return {
        type: 'text',
        inputMode: 'decimal',
        autoCapitalize: 'off',
        autoCorrect: 'off',
        autoComplete: 'off',
      };

    case 'Password':
    case 'password':
      return {
        type: 'password',
        inputMode: 'text',
        autoCapitalize: 'off',
        autoCorrect: 'off',
        autoComplete: 'current-password',
      };

    case 'Text':
    case 'text':
    case 'string':
    default:
      return {
        type: 'text',
        inputMode: 'text',
        autoCapitalize: 'sentences',
        autoCorrect: 'on',
        autoComplete: 'off',
      };
  }
}

/**
 * Validates field value against field type rules and custom regex.
 * Accepts fieldSubtype (v2) in addition to fieldType (v1) for type mapping.
 */
/**
 * Reads a compiled numeric answer. The compiler's numeric `validationRegex` is
 * `^-?\d+([.,]\d+)?$`, so a comma decimal separator is a VALID answer and must
 * parse as one — `Number('70,5')` is `NaN`, which would have silently skipped
 * the range check for every locale that types a comma (W8 d).
 */
function parseNumericAnswer(value: string): number | null {
  const normalized = value.replace(',', '.');
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

export function validateFieldValue(
  value: string,
  fieldType?: string,
  validation?: { required?: boolean; regex?: string; min?: number; max?: number }
): ValidationResult {
  const trimmedValue = value?.trim() ?? '';

  if (validation?.required && !trimmedValue) {
    return { isValid: false, error: 'This field is required' };
  }

  if (!trimmedValue) {
    return { isValid: true };
  }

  switch (fieldType) {
    case 'Email':
    case 'email':
      if (!EMAIL_REGEX.test(trimmedValue)) {
        return { isValid: false, error: 'Please enter a valid email address' };
      }
      break;

    case 'Phone':
    case 'phone':
      if (!PHONE_REGEX.test(trimmedValue)) {
        return { isValid: false, error: 'Please enter a valid phone number' };
      }
      break;

    case 'Number':
    case 'number':
      if (isNaN(Number(trimmedValue)) || !isFinite(Number(trimmedValue))) {
        return { isValid: false, error: 'Please enter a valid number' };
      }
      break;
  }

  if (validation?.regex) {
    try {
      const regex = buildValidationRegExp(validation.regex);
      if (!regex.test(trimmedValue)) {
        return { isValid: false, error: 'Invalid format' };
      }
    } catch (e) {
      console.warn('[Field.utils] Invalid regex pattern:', validation.regex, e);
    }
  }

  // W8(d) / D-W2-3 — the RANGE, after the shape. `min`/`max` are inclusive
  // bounds the compiler materializes onto a numeric `TextFieldConfig`
  // (schema-models 3.1.0-strategy.3). A value that is not a number at all is
  // NOT reported here: the field-type and regex checks above own that refusal,
  // and an out-of-bounds message naming numbers the visitor never typed would
  // be a worse error than the one it replaced.
  const { min, max } = validation ?? {};
  if (min !== undefined || max !== undefined) {
    const numeric = parseNumericAnswer(trimmedValue);
    if (numeric !== null) {
      const belowMin = min !== undefined && numeric < min;
      const aboveMax = max !== undefined && numeric > max;
      if (belowMin || aboveMax) {
        const bound = min !== undefined && max !== undefined
          ? `between ${min} and ${max}`
          : min !== undefined ? `${min} or more` : `${max} or less`;
        return { isValid: false, error: `Please enter a number ${bound}` };
      }
    }
  }

  return { isValid: true };
}
